import React, { ReactNode, useContext } from 'react'
import { useLocation } from '@reach/router'
import UserContext from '../../UserContext'
import type { User } from '../../UserContext'
import type { ServiceRequestMember } from '../../models'
import AppDocument from '../../models/AppDocument'
import { difference } from 'lodash'
import { ApiPost } from './Api'
import { getCollectedMeta } from '../../watchtower'

const configData = window.CS_CONFIG

// declare let ga: (cmd: string, parm: string, value?:string) => void;

export type AnalyticsEvent<T> = {
    name: string
    data: T
}

export type EventParams = {
    description?: string
    event_label?: string
    event_category?: string
    page_title?: string
  }

export type TrackedProps = { children?: ReactNode, onClick?: () => void, event: string}

export const TrackedButton: React.FC<TrackedProps> = ({ children, onClick, event }) => {
    const user = useContext(UserContext)
    const { pathname } = useLocation()
    
    const handleClick = () => {
        SaveEvent(ClickEvent(user, pathname, event))
        
        if (onClick) 
            onClick()
    }

    return <span onClick={handleClick}>{children}</span>
}

export type TabChange = EventParams & {
    // GA4 version (limit 4 custom dimensions)
    //
    // area: string
    // tab: string
    // previous_tab?: string
    // extra: string
}

export const TabChangeEvent = (user: User, area: string, tab: string, previousTab?: string): AnalyticsEvent<TabChange> | null => {
    return tab !== previousTab ? {
        name: 'Change_Tab', data: {
            event_category: ScrubArea(area),
            event_label: `${GetUserState(user)}_${GetUserRole(user)}_Tab_${tab}_Prev_${previousTab ? previousTab : ''}`
        }

        // GA4 version (limit 4 custom dimensions)
        //
        // data: {
        //   area: area,
        //   tab: tab,
        //   previous_tab: previousTab
        //   extra: GetUserState(user) + '_' + GetUserRole(user)
        // }
    } : null
}

export type ModalChange = EventParams & {
    // GA4 version (limit 4 custom dimensions)
    //
    // area: string
    // extra: string
}

export const ModalChangeEvent = (user: User, area: string, action: string): AnalyticsEvent<ModalChange> => {
    return {
        name: `${action}_Modal`, data: {
            event_category: ScrubArea(area),
            event_label: `${GetUserState(user)}_${GetUserRole(user)}`
        }

        // GA4 version (limit 4 custom dimensions)
        //
        // data: {
        //   area: area,
        //   extra: GetUserState(user) + '_' + GetUserRole(user)
        // }
    }
}

type ButtonClickEvent = EventParams & {
    // GA4 version (limit 4 custom dimensions)
    //
    // area: string
    // extra: string
}

export const ClickEvent = (user: User, area: string, button: string, additionalInfo?: string | ServiceRequestMember): AnalyticsEvent<ButtonClickEvent> => {
    let info: string | null = null
    
    if (typeof additionalInfo === 'string')
        info = additionalInfo
    else if (additionalInfo)  {
        const member = additionalInfo
        info = `COUNTY_${ScrubCounty(member.county ? member.county : '')}_PLAN_${member.plan ? member.plan : ''}`
    }

    return {
        name: `Click_${button}`, data: {
            event_category: ScrubArea(area),
            event_label: `${GetUserState(user)}_${GetUserRole(user)}_BTN_${button}${info ? `_INFO_${info}` : ''}`
        }

        // GA4 version (limit 4 custom dimensions)
        //
        // data: {
        //   area: area,
        //   extra: GetUserState(user) + '_' + GetUserRole(user)
        // }
    }
}

export type PageChange = EventParams & {
    // GA4 version (limit 4 custom dimensions)
    //
    // area: string
    // size?: number
    // page?: number
    // extra: string
}

export const PageChangeEvent = (user: User, area: string, type: string, pageOrSize: number): AnalyticsEvent<PageChange> => {
    switch(type) {
        case 'next':
            return {
                name: 'Change_Page_Next', data: {
                    event_category: ScrubArea(area),
                    event_label: `${GetUserState(user)}_${GetUserRole(user)}_Page_${pageOrSize}`
                }
            }
        case 'prev':
            return {
                name: 'Change_Page_Prev', data: {
                    event_category: ScrubArea(area),
                    event_label: `${GetUserState(user)}_${GetUserRole(user)}_Page_${pageOrSize}`
                }
            }
        case 'size':
            return {
                name: 'Change_Page_Size', data: {
                    event_category: ScrubArea(area),
                    event_label: `${GetUserState(user)}_${GetUserRole(user)}_Size_${pageOrSize}`
                }
            }
        default:
            return {
                name: 'Change_Page', data: {
                    event_category: ScrubArea(area),
                    event_label: `${GetUserState(user)}_${GetUserRole(user)}_Page_${pageOrSize}`
                }
            }
    }
}

type FilterChange = EventParams & {
    // GA4 version (limit 4 custom dimensions)
    //
    // area: string
    // counties: string
    // specialties: string
    // extra: string
}

export const FilterChangeEvent = (user: User, area: string, counties?: string[], specialties?: string[]): AnalyticsEvent<FilterChange> => {
    return {
        name: 'Filter_Change', data: {
            event_category: ScrubArea(area),
            event_label: `${GetUserState(user)}_${GetUserRole(user)}_COUNTY_${counties ? counties.join() : ''}_SPECIALTY_${specialties ? specialties.join() : ''}`
        }

        // GA4 version (limit 4 custom dimensions)
        //
        // data: {
        //   area: area,
        //   counties: counties ? counties.join() : ''
        //   specialties: specialties ? specialties.join() : ''
        //   extra: GetUserState(user) + '_' + GetUserRole(user)
        // }
    }
}

type DocumentActionEvent = EventParams & {
    // GA4 version (limit 4 custom dimensions)
    //
    // area: string
    // doc: doc?.documentId
    // extra: string
}

export const DocumentEvent = (user: User, area: string, type: string, member?: ServiceRequestMember, doc?: AppDocument): AnalyticsEvent<DocumentActionEvent> => {
    const info = `COUNTY_${ScrubCounty(member && member.county ? member.county : 'Any')}_PLAN_${member && member.plan ? member.plan : 'Any'}_Doc_${doc ? doc.documentId : ''}`

    switch(type) {
        case 'view':
            return {
                name: 'View_Doc', data: {
                    event_category: ScrubArea(area),
                    event_label: `${GetUserState(user)}_${GetUserRole(user)}_${info}`
                }
            }
        case 'add':
            return {
                name: 'Add_Doc', data: {
                    event_category: ScrubArea(area),
                    event_label: `${GetUserState(user)}_${GetUserRole(user)}_${info}`
                }
            }
        case 'delete':
            return {
                name: 'Delete_Doc', data: {
                    event_category: ScrubArea(area),
                    event_label: `${GetUserState(user)}_${GetUserRole(user)}_${info}`
                }
            }
        case 'cancel':
            return {
                name: 'Cancel_Delete_Doc', data: {
                    event_category: ScrubArea(area),
                    event_label: `${GetUserState(user)}_${GetUserRole(user)}_${info}`
                }
            }
        default:
            return {
                name: 'View_Doc', data: {
                    event_category: ScrubArea(area),
                    event_label: `${GetUserState(user)}_${GetUserRole(user)}_${info}`
                }
            }
    }

    // GA4 version (limit 4 custom dimensions)
    //
    // data: {
    //   area: area,
    //   doc: doc?.documentId
    //   extra: GetUserState(user) + '_' + GetUserRole(user)
    // }
}

const ScrubCounty = (name: string): string => {
    // eslint-disable-next-line no-useless-escape
    return name.toLowerCase().replace(/^[a-zA-Z]/g, (match) => {
        // eslint-disable-next-line no-useless-escape
        return match.toUpperCase()
    })
}

const ScrubArea = (name: string): string => {
    // eslint-disable-next-line no-useless-escape
    return name.replace(/(?:^|[/\-_ ])[a-zA-Z]/g, (match) => {
        // eslint-disable-next-line no-useless-escape
        return match.toUpperCase().replace(/[\-_]/, ' ').replace('/', '')
    }).replace(/\?.*/g, '')
}

const GetUserState = (user: User): string => {
    return !!user.providerCounties && user.providerCounties.length > 0 ? user.providerCounties[0].state : 'Any'
}

const GetUserRole = (user: User): string => {
    const filteredRoles = difference(user.roles, ['Impersonator'])
    return filteredRoles.length > 0 ? filteredRoles[0] : 'Any'
}

export const CheckboxEvent = (user: User, area: string, value: string, checked: boolean): AnalyticsEvent<EventParams> => ({
    name: 'Check_Preference',
    data: {
        event_category: ScrubArea(area),
        event_label: `${GetUserState(user)}_${GetUserRole(user)}_${value}_${checked ? 'Checked' : 'Unchecked'}`
    }
})

export const SelectEvent = (user: User, area: string, value: string): AnalyticsEvent<EventParams> => ({
    name: 'Select_Preference',
    data: {
        event_category: ScrubArea(area),
        event_label: `${GetUserState(user)}_${GetUserRole(user)}_${value}`
    }
})

export const SaveEvent = <T extends EventParams>(event: AnalyticsEvent<T> | null): void => {
    if (event) {
        const enriched = {
            ...event,
            data: { ...event.data, page_title: document.title }
        }

        if (configData.Env !== 'production') {
            // eslint-disable-next-line no-console
            console.log('GTag', { 'event': enriched.name, 'parameters': enriched.data })
        }

        ApiPost('Watchtower', undefined, { ...enriched, collectedMeta: getCollectedMeta() })
        // eslint-disable-next-line no-console
        .catch(console.log)
    }
}

export const PageView = (pathAndQuery: string): void => {
    const pageTitle = document.title.replace(/\|.*/, `| ${ScrubArea(pathAndQuery)}`)
    document.title = pageTitle

    if (configData.Env !== 'production') {
        // eslint-disable-next-line no-console
        console.log('GTag', { page_title: pageTitle, page_path: pathAndQuery })
    }

    ApiPost('Watchtower', undefined, { name: 'page_view', data: { page_title: pageTitle, page_path: pathAndQuery }, collectedMeta: getCollectedMeta() })
    // eslint-disable-next-line no-console
    .catch(console.log)
}
